/* Telegram Mini App bridge (DESIGN14 §F1.2).

   A single typed door to window.Telegram.WebApp with a faithful DEV MOCK
   so the whole TMA is testable in a plain browser. Real Telegram always
   wins when present; the mock activates ONLY in development AND only when
   localStorage['tma_mock'] holds a JSON blob:

     localStorage.tma_mock = JSON.stringify({
       initData: '<a real signed initData string>',   // required
       colorScheme: 'dark',                            // optional
       themeParams: { bg_color: '#17212b', ... }       // optional
     })

   The mock renders a real, clickable MainButton / BackButton into the DOM
   (fixed, themed) so MainButton-driven flows — generate, approve — are
   fully exercisable without Telegram. In real Telegram those controls are
   native chrome and the mock DOM is never created.

   Helpers: applyTheme() maps themeParams onto CSS custom props the whole
   surface reads; haptic / showConfirm wrap the native APIs with graceful
   fallbacks; useBackButton / useMainButton are React helpers that own a
   control for as long as a screen is mounted and clean up on unmount. */

import { useEffect, useRef } from 'react';

/* ─────────────────────────── types ─────────────────────────── */

export interface ThemeParams {
  bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  secondary_bg_color?: string;
  header_bg_color?: string;
  accent_text_color?: string;
  section_bg_color?: string;
  section_header_text_color?: string;
  section_separator_color?: string;
  subtitle_text_color?: string;
  destructive_text_color?: string;
}

export type HapticImpactStyle = 'light' | 'medium' | 'heavy' | 'rigid' | 'soft';
export type HapticNotificationType = 'error' | 'success' | 'warning';

export interface WebAppBackButton {
  isVisible: boolean;
  show(): void;
  hide(): void;
  onClick(cb: () => void): void;
  offClick(cb: () => void): void;
}

export interface MainButtonParams {
  text?: string;
  color?: string;
  text_color?: string;
  is_active?: boolean;
  is_visible?: boolean;
}

export interface WebAppMainButton {
  text: string;
  isVisible: boolean;
  isActive: boolean;
  isProgressVisible: boolean;
  show(): void;
  hide(): void;
  enable(): void;
  disable(): void;
  showProgress(leaveActive?: boolean): void;
  hideProgress(): void;
  setText(text: string): void;
  onClick(cb: () => void): void;
  offClick(cb: () => void): void;
  setParams(params: MainButtonParams): void;
}

export interface WebAppHaptic {
  impactOccurred(style: HapticImpactStyle): void;
  notificationOccurred(type: HapticNotificationType): void;
  selectionChanged(): void;
}

export interface TelegramWebApp {
  initData: string;
  initDataUnsafe: Record<string, unknown>;
  version: string;
  platform: string;
  colorScheme: 'light' | 'dark';
  themeParams: ThemeParams;
  isExpanded: boolean;
  viewportHeight: number;
  viewportStableHeight: number;
  headerColor?: string;
  backgroundColor?: string;
  BackButton: WebAppBackButton;
  MainButton: WebAppMainButton;
  HapticFeedback: WebAppHaptic;
  ready(): void;
  expand(): void;
  close(): void;
  onEvent(type: string, cb: (...args: unknown[]) => void): void;
  offEvent(type: string, cb: (...args: unknown[]) => void): void;
  showConfirm(message: string, cb: (ok: boolean) => void): void;
  showAlert(message: string, cb?: () => void): void;
  setHeaderColor?(color: string): void;
  setBackgroundColor?(color: string): void;
  openTelegramLink?(url: string): void;
  openLink?(url: string, options?: { try_instant_view?: boolean }): void;
  isVersionAtLeast?(v: string): boolean;
  /** Bot API 8.0+: Telegram's own downloader (asks the user, then saves). */
  downloadFile?(params: { url: string; file_name: string }, cb?: (accepted: boolean) => void): void;
  /** Bot API 7.7+: stop a vertical drag from collapsing the mini app. */
  disableVerticalSwipes?(): void;
  enableVerticalSwipes?(): void;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

/* ───────────────────── theme fallbacks ─────────────────────── */

const LIGHT_DEFAULTS: Required<Pick<
  ThemeParams,
  | 'bg_color'
  | 'text_color'
  | 'hint_color'
  | 'link_color'
  | 'button_color'
  | 'button_text_color'
  | 'secondary_bg_color'
  | 'section_bg_color'
  | 'section_separator_color'
  | 'subtitle_text_color'
  | 'destructive_text_color'
>> = {
  bg_color: '#f5f7fa',
  text_color: '#21344a',
  hint_color: '#718198',
  link_color: '#4f7898',
  button_color: '#294760',
  button_text_color: '#ffffff',
  secondary_bg_color: '#ffffff',
  section_bg_color: '#ffffff',
  section_separator_color: '#e1e7ee',
  subtitle_text_color: '#718198',
  destructive_text_color: '#c4535c',
};

const DARK_DEFAULTS: typeof LIGHT_DEFAULTS = {
  bg_color: '#152333',
  text_color: '#f0f5f8',
  hint_color: '#9aadc0',
  link_color: '#a9cee8',
  button_color: '#507d9d',
  button_text_color: '#ffffff',
  secondary_bg_color: '#1c2e3f',
  section_bg_color: '#1c2e3f',
  section_separator_color: '#34495b',
  subtitle_text_color: '#9aadc0',
  destructive_text_color: '#e37880',
};

// LATO brand accent — theme-INDEPENDENT so the product reads the same in
// light and dark. Used for the wordmark, active pills, focus rings.
const ACCENT = '#4f7898';
const ACCENT_2 = '#8eb5ce';

/* ───────────────────────── the mock ────────────────────────── */

interface MockConfig {
  initData?: string;
  colorScheme?: 'light' | 'dark';
  themeParams?: ThemeParams;
}

// A stateful DOM-rendered control so a browser dev session can actually
// press the MainButton / BackButton. Kept private to the mock.
/** Dev-mock confirm/alert rendered INTO THE DOM, mirroring how the mock draws
    a real MainButton. Telegram's own showConfirm is a native in-app dialog;
    the obvious stand-in — window.confirm — is a blocking browser dialog that
    automation and headless previews suppress, so a confirmed action would
    silently never fire while it looks like the app ignored the tap. A DOM
    dialog behaves like the real one for both a human and a driver. Mock only:
    real Telegram always wins (see showConfirm below). */
function domDialog(message: string, withCancel: boolean, cb: (ok: boolean) => void): void {
  const wrap = document.createElement('div');
  wrap.setAttribute('data-tma-mock', 'dialog');
  wrap.style.cssText =
    'position:fixed;inset:0;z-index:2147483600;display:flex;align-items:center;' +
    'justify-content:center;padding:24px;background:rgba(15,23,42,.45);';
  const card = document.createElement('div');
  card.style.cssText =
    'max-width:320px;width:100%;background:#fff;color:#0f172a;border-radius:14px;' +
    'padding:18px 18px 12px;font:15px/1.45 -apple-system,system-ui,sans-serif;' +
    'box-shadow:0 18px 50px rgba(0,0,0,.3);';
  const text = document.createElement('div');
  text.textContent = message;
  text.style.cssText = 'margin-bottom:14px;';
  const row = document.createElement('div');
  row.style.cssText = 'display:flex;gap:8px;justify-content:flex-end;';
  const done = (ok: boolean) => {
    wrap.remove();
    cb(ok);
  };
  if (withCancel) {
    const cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.textContent = 'Cancel';
    cancel.setAttribute('data-tma-mock', 'dialog-cancel');
    cancel.style.cssText =
      'appearance:none;border:none;background:transparent;color:#64748b;font:600 15px/1 inherit;' +
      'padding:10px 14px;border-radius:9px;cursor:pointer;';
    cancel.addEventListener('click', () => done(false));
    row.appendChild(cancel);
  }
  const ok = document.createElement('button');
  ok.type = 'button';
  ok.textContent = 'OK';
  ok.setAttribute('data-tma-mock', 'dialog-ok');
  ok.style.cssText =
    'appearance:none;border:none;background:#294760;color:#fff;font:600 15px/1 inherit;' +
    'padding:10px 18px;border-radius:9px;cursor:pointer;';
  ok.addEventListener('click', () => done(true));
  row.appendChild(ok);
  card.appendChild(text);
  card.appendChild(row);
  wrap.appendChild(card);
  document.body.appendChild(wrap);
  ok.focus();
}

function makeDomMainButton(): WebAppMainButton {
  let handler: (() => void) | null = null;
  let el: HTMLButtonElement | null = null;
  const state = {
    text: 'CONTINUE',
    isVisible: false,
    isActive: true,
    isProgressVisible: false,
  };
  const ensure = () => {
    if (el) return el;
    const b = document.createElement('button');
    b.type = 'button';
    b.setAttribute('data-tma-mock', 'main-button');
    b.style.cssText =
      'position:fixed;left:12px;right:12px;bottom:calc(10px + env(safe-area-inset-bottom));' +
      'height:52px;border:none;border-radius:12px;font:600 16px/1 -apple-system,system-ui,sans-serif;' +
      'z-index:2147483000;cursor:pointer;box-shadow:0 6px 20px rgba(0,0,0,.22);transition:opacity .15s,transform .1s;';
    b.addEventListener('click', () => {
      if (state.isActive && handler) handler();
    });
    document.body.appendChild(b);
    el = b;
    return b;
  };
  const paint = () => {
    const b = ensure();
    const cs = getComputedStyle(document.documentElement);
    b.textContent = state.isProgressVisible ? '…' : state.text;
    b.style.background = cs.getPropertyValue('--tma-button').trim() || ACCENT;
    b.style.color = cs.getPropertyValue('--tma-button-text').trim() || '#fff';
    b.style.display = state.isVisible ? 'block' : 'none';
    b.style.opacity = state.isActive ? '1' : '.55';
    b.disabled = !state.isActive;
  };
  return {
    get text() {
      return state.text;
    },
    get isVisible() {
      return state.isVisible;
    },
    get isActive() {
      return state.isActive;
    },
    get isProgressVisible() {
      return state.isProgressVisible;
    },
    show() {
      state.isVisible = true;
      paint();
    },
    hide() {
      state.isVisible = false;
      paint();
    },
    enable() {
      state.isActive = true;
      paint();
    },
    disable() {
      state.isActive = false;
      paint();
    },
    showProgress() {
      state.isProgressVisible = true;
      paint();
    },
    hideProgress() {
      state.isProgressVisible = false;
      paint();
    },
    setText(t: string) {
      state.text = t;
      paint();
    },
    onClick(cb: () => void) {
      handler = cb;
    },
    offClick(cb: () => void) {
      if (handler === cb) handler = null;
    },
    setParams(p: MainButtonParams) {
      if (p.text != null) state.text = p.text;
      if (p.is_active != null) state.isActive = p.is_active;
      if (p.is_visible != null) state.isVisible = p.is_visible;
      paint();
    },
  };
}

function makeDomBackButton(): WebAppBackButton {
  let handler: (() => void) | null = null;
  let el: HTMLButtonElement | null = null;
  const state = { isVisible: false };
  const ensure = () => {
    if (el) return el;
    const b = document.createElement('button');
    b.type = 'button';
    b.setAttribute('data-tma-mock', 'back-button');
    b.textContent = '‹ Back';
    b.style.cssText =
      'position:fixed;left:10px;top:calc(8px + env(safe-area-inset-top));height:34px;padding:0 12px;' +
      'border:none;border-radius:9px;background:rgba(127,127,127,.16);color:inherit;' +
      'font:600 14px/1 -apple-system,system-ui,sans-serif;z-index:2147483000;cursor:pointer;';
    b.addEventListener('click', () => handler && handler());
    document.body.appendChild(b);
    el = b;
    return b;
  };
  const paint = () => {
    ensure().style.display = state.isVisible ? 'block' : 'none';
  };
  return {
    get isVisible() {
      return state.isVisible;
    },
    show() {
      state.isVisible = true;
      paint();
    },
    hide() {
      state.isVisible = false;
      paint();
    },
    onClick(cb: () => void) {
      handler = cb;
    },
    offClick(cb: () => void) {
      if (handler === cb) handler = null;
    },
  };
}

function buildMock(): TelegramWebApp | null {
  if (process.env.NODE_ENV !== 'development') return null;
  let raw: string | null = null;
  try {
    raw = localStorage.getItem('tma_mock');
  } catch {
    return null;
  }
  if (!raw) return null;
  let cfg: MockConfig = {};
  try {
    cfg = JSON.parse(raw) as MockConfig;
  } catch {
    cfg = {};
  }
  const colorScheme: 'light' | 'dark' = cfg.colorScheme === 'dark' ? 'dark' : 'light';
  const themeParams: ThemeParams =
    cfg.themeParams || (colorScheme === 'dark' ? DARK_DEFAULTS : LIGHT_DEFAULTS);
  const listeners: Record<string, Array<(...a: unknown[]) => void>> = {};
  const mock: TelegramWebApp = {
    initData: cfg.initData || '',
    initDataUnsafe: {},
    version: '7.0',
    platform: 'mock',
    colorScheme,
    themeParams,
    isExpanded: true,
    viewportHeight: window.innerHeight,
    viewportStableHeight: window.innerHeight,
    BackButton: makeDomBackButton(),
    MainButton: makeDomMainButton(),
    HapticFeedback: {
      impactOccurred() {},
      notificationOccurred() {},
      selectionChanged() {},
    },
    ready() {},
    expand() {},
    close() {},
    onEvent(type, cb) {
      (listeners[type] = listeners[type] || []).push(cb);
    },
    offEvent(type, cb) {
      listeners[type] = (listeners[type] || []).filter((f) => f !== cb);
    },
    showConfirm(message, cb) {
      domDialog(message, true, cb);
    },
    showAlert(message, cb) {
      domDialog(message, false, () => cb && cb());
    },
    setHeaderColor() {},
    setBackgroundColor() {},
    openTelegramLink(url) {
      window.open(url, '_blank');
    },
    openLink(url) {
      window.open(url, '_blank');
    },
    isVersionAtLeast() {
      return true;
    },
  };
  return mock;
}

/* ─────────────────────── resolution ────────────────────────── */

let cached: TelegramWebApp | null | undefined;

/** The active WebApp: real Telegram if present, else the dev mock, else
    null (opened outside Telegram with no mock). Resolved once. */
/* ─────────────────────── the SDK itself ─────────────────────── */

const SDK_URL = 'https://telegram.org/js/telegram-web-app.js';
let sdkPromise: Promise<boolean> | null = null;

/** Telegram does NOT inject window.Telegram.WebApp into a mini app — the
    page has to load the SDK script itself, and nothing here did: every
    earlier check ran on the dev mock, which masked it, so the first real
    open showed «Open in Telegram» inside Telegram. Loads the SDK once (no
    network when a host or the dev mock is already present), resolves when
    it is usable or has failed, and never rejects — the boot decides what a
    missing host means. */
export function loadTelegramSdk(): Promise<boolean> {
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise<boolean>((resolve) => {
    if (typeof window === 'undefined') {
      resolve(false);
      return;
    }
    if (window.Telegram && window.Telegram.WebApp) {
      resolve(true);
      return;
    }
    if (process.env.NODE_ENV === 'development') {
      try {
        if (localStorage.getItem('tma_mock')) {
          resolve(true);
          return;
        }
      } catch {
        /* storage blocked — fall through to the real SDK */
      }
    }
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      cached = undefined; // re-detect now that the host object may exist
      resolve(!!(window.Telegram && window.Telegram.WebApp));
    };
    const script = document.createElement('script');
    script.src = SDK_URL;
    script.async = true;
    script.onload = done;
    script.onerror = done;
    document.head.appendChild(script);
    window.setTimeout(done, 8000);
  });
  return sdkPromise;
}

export function getWebApp(): TelegramWebApp | null {
  if (cached !== undefined) return cached;
  const real =
    typeof window !== 'undefined' && window.Telegram && window.Telegram.WebApp
      ? window.Telegram.WebApp
      : null;
  cached = real || buildMock();
  return cached;
}

/** True when a real Telegram host is present (not the mock, not bare). */
export function isRealTelegram(): boolean {
  return !!(
    typeof window !== 'undefined' &&
    window.Telegram &&
    window.Telegram.WebApp &&
    // A real host always carries a version string; the bare object some
    // proxies inject does not.
    window.Telegram.WebApp.initData !== undefined
  );
}

/** True when we have SOMETHING to run against (real host or dev mock). */
export function hasWebApp(): boolean {
  return getWebApp() != null;
}

/* ─────────────────────── theme plumbing ────────────────────── */

/** Map the host themeParams onto the CSS custom props the TMA reads. Called
    at boot and again on every themeChanged event. Fallbacks keep the surface
    legible even when a host omits a colour. */
export function applyTheme(app: TelegramWebApp | null): void {
  const root = document.documentElement;
  const scheme = app?.colorScheme === 'dark' ? 'dark' : 'light';
  const base = scheme === 'dark' ? DARK_DEFAULTS : LIGHT_DEFAULTS;
  const tp = app?.themeParams || {};
  const set = (name: string, val: string | undefined, fallback: string) =>
    root.style.setProperty(name, (val && String(val)) || fallback);

  set('--tma-bg', tp.bg_color, base.bg_color);
  set('--tma-text', tp.text_color, base.text_color);
  set('--tma-hint', tp.hint_color, base.hint_color);
  set('--tma-link', tp.link_color, base.link_color);
  set('--tma-button', tp.button_color, base.button_color);
  set('--tma-button-text', tp.button_text_color, base.button_text_color);
  set('--tma-secondary-bg', tp.secondary_bg_color, base.secondary_bg_color);
  set('--tma-section-bg', tp.section_bg_color, base.section_bg_color);
  set('--tma-separator', tp.section_separator_color, base.section_separator_color);
  set('--tma-subtitle', tp.subtitle_text_color, base.subtitle_text_color);
  set('--tma-destructive', tp.destructive_text_color, base.destructive_text_color);
  root.style.setProperty('--tma-accent', ACCENT);
  root.style.setProperty('--tma-accent-2', ACCENT_2);
  root.setAttribute('data-tma-scheme', scheme);

  // Paint the Telegram header/background to match so the native chrome
  // blends into the surface (no-op on the mock).
  try {
    if (app?.setBackgroundColor) app.setBackgroundColor(tp.bg_color || base.bg_color);
    if (app?.setHeaderColor) app.setHeaderColor(tp.secondary_bg_color || base.secondary_bg_color);
  } catch {
    /* older host — ignore */
  }
}

/** Reflect the live viewport height into a CSS var so keyboard-aware layout
    (the pinned prompt bar) tracks the shrinking viewport. Returns a cleanup. */
export function watchViewport(app: TelegramWebApp | null): () => void {
  const root = document.documentElement;
  const apply = () => {
    const h = app?.viewportHeight || window.innerHeight;
    root.style.setProperty('--tma-vh', h + 'px');
    const stable = app?.viewportStableHeight || h;
    root.style.setProperty('--tma-vh-stable', stable + 'px');
  };
  apply();
  const onResize = () => apply();
  app?.onEvent('viewportChanged', onResize);
  window.addEventListener('resize', onResize);
  return () => {
    app?.offEvent('viewportChanged', onResize);
    window.removeEventListener('resize', onResize);
  };
}

/** Subscribe to host theme changes; returns a cleanup. */
export function watchTheme(app: TelegramWebApp | null): () => void {
  const onTheme = () => applyTheme(app);
  app?.onEvent('themeChanged', onTheme);
  return () => app?.offEvent('themeChanged', onTheme);
}

/* ───────────────────── haptics + dialogs ───────────────────── */

export const haptic = {
  impact(style: HapticImpactStyle = 'light'): void {
    try {
      getWebApp()?.HapticFeedback.impactOccurred(style);
    } catch {
      /* unsupported host — silent */
    }
  },
  notify(type: HapticNotificationType): void {
    try {
      getWebApp()?.HapticFeedback.notificationOccurred(type);
    } catch {
      /* silent */
    }
  },
  select(): void {
    try {
      getWebApp()?.HapticFeedback.selectionChanged();
    } catch {
      /* silent */
    }
  },
};

/** Confirm via the native Telegram dialog when available, else window.confirm.
    Always resolves (true = confirmed). */
export function showConfirm(message: string): Promise<boolean> {
  const app = getWebApp();
  if (app && typeof app.showConfirm === 'function') {
    return new Promise((resolve) => {
      try {
        app.showConfirm(message, (ok) => resolve(!!ok));
      } catch {
        resolve(window.confirm(message));
      }
    });
  }
  return Promise.resolve(window.confirm(message));
}

/* ─────────────────── React control helpers ─────────────────── */

/** Own the BackButton for as long as `visible` is true and the component is
    mounted. The latest onClick is always used (kept in a ref) so callers can
    pass an inline closure without re-registering every render. */
/* An overlay (the full-screen poster viewer) may CLAIM the next Back press
   without owning the button: the screen keeps the button shown and its own
   handler, and the top-most interceptor answers first. */
const backInterceptors: Array<() => void> = [];

export function useBackInterceptor(active: boolean, onBack: () => void): void {
  const cbRef = useRef(onBack);
  cbRef.current = onBack;
  useEffect(() => {
    if (!active) return undefined;
    const fn = () => cbRef.current();
    backInterceptors.push(fn);
    return () => {
      const i = backInterceptors.indexOf(fn);
      if (i >= 0) backInterceptors.splice(i, 1);
    };
  }, [active]);
}

export function useBackButton(visible: boolean, onClick: () => void): void {
  const cbRef = useRef(onClick);
  cbRef.current = onClick;
  useEffect(() => {
    const app = getWebApp();
    if (!app) return undefined;
    const handler = () => {
      const top = backInterceptors[backInterceptors.length - 1];
      if (top) top();
      else cbRef.current();
    };
    if (visible) {
      app.BackButton.onClick(handler);
      app.BackButton.show();
    } else {
      app.BackButton.hide();
    }
    return () => {
      app.BackButton.offClick(handler);
      app.BackButton.hide();
    };
  }, [visible]);
}

export interface MainButtonState {
  text: string;
  visible: boolean;
  /** Greyed + non-interactive when false. Default true. */
  enabled?: boolean;
  /** Show the in-button spinner. Default false. */
  progress?: boolean;
  onClick: () => void;
}

// Space (px) the MainButton occupies at the bottom so scroll regions can
// pad themselves out of its way. Mirrors the mock button height + margin.
const MAIN_BUTTON_SPACE = '72px';

/** Own the MainButton for as long as the component is mounted, reflecting the
    given state. Cleans up (hide + offClick) on unmount so the next screen
    starts from a blank button. Also toggles the --tma-mb-space var so the
    active scroll region can reserve room. */
export function useMainButton(state: MainButtonState): void {
  const cbRef = useRef(state.onClick);
  cbRef.current = state.onClick;
  const { text, visible, enabled = true, progress = false } = state;
  useEffect(() => {
    const app = getWebApp();
    if (!app) return undefined;
    const mb = app.MainButton;
    const handler = () => cbRef.current();
    mb.onClick(handler);
    return () => {
      try {
        mb.offClick(handler);
        mb.hide();
      } catch {
        /* host quirk */
      }
      document.documentElement.style.setProperty('--tma-mb-space', '0px');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const app = getWebApp();
    if (!app) return;
    const mb = app.MainButton;
    // The REAL SDK throws on an empty label («Bottom button text is
    // required» → WebAppBottomButtonParamInvalid) and an uncaught throw here
    // unmounts the whole React tree — the black screen the owner hit the
    // first time a project was opened on a phone. The dev mock swallowed it.
    // So: never hand the host an empty label, and never let a host quirk
    // escape this hook.
    const safe = (f: () => void) => {
      try {
        f();
      } catch {
        /* host quirk — the button is chrome, never worth the tree */
      }
    };
    const label = text.trim();
    if (label) {
      safe(() => mb.setText(label));
      // setParams is the documented way to relabel a shown button; some
      // Android builds paint a blank bar when only setText runs mid-show.
      safe(() => mb.setParams({ text: label }));
    }
    safe(() => (enabled ? mb.enable() : mb.disable()));
    safe(() => (progress ? mb.showProgress() : mb.hideProgress()));
    const show = visible && !!label;
    safe(() => (show ? mb.show() : mb.hide()));
    document.documentElement.style.setProperty(
      '--tma-mb-space',
      show ? MAIN_BUTTON_SPACE : '0px',
    );
  }, [text, visible, enabled, progress]);
}
